/* ============================================================
   NOMA — global.js
   Custom elements: header, drawers, reveals, hero carousel
   ============================================================ */

(() => {
  'use strict';

  /* ----- utils ----- */
  const $ = (s, ctx = document) => ctx.querySelector(s);
  const $$ = (s, ctx = document) => Array.from(ctx.querySelectorAll(s));

  // Scroll lock with stacking — multiple drawers can lock; only the last unlock releases
  let scrollLockCount = 0;
  const lockScroll = () => {
    scrollLockCount++;
    if (scrollLockCount === 1) {
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }
  };
  const unlockScroll = () => {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }
  };

  const formatMoney = (cents) => {
    const amount = (cents / 100);
    const format = window.theme?.shop?.moneyFormat || '€{{amount}}';
    const formatted = amount.toLocaleString(document.documentElement.lang || 'fr-FR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return format.replace(/\{\{\s*amount[_a-z]*\s*\}\}/g, formatted);
  };
  window.formatMoney = formatMoney;

  /* ============================================================
     Scroll reveal — IntersectionObserver
     ============================================================ */
  const initReveal = () => {
    const targets = $$('[data-reveal], [data-reveal-stagger]');
    if (!targets.length || !('IntersectionObserver' in window)) {
      targets.forEach(t => t.classList.add('is-revealed'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    targets.forEach(t => io.observe(t));
  };

  /* ============================================================
     Header scroll state + transparent override
     ============================================================ */
  const initHeader = () => {
    const header = $('.site-header');
    if (!header) return;
    let last = 0;
    const sync = () => {
      const y = window.scrollY;
      if (y > 8) header.classList.add('is-scrolled');
      else header.classList.remove('is-scrolled');
      last = y;
    };
    sync();
    window.addEventListener('scroll', sync, { passive: true });
  };

  /* ============================================================
     Mobile menu drawer
     ============================================================ */
  class MobileMenu {
    constructor() {
      this.menu = $('.mobile-menu');
      this.scrim = $('.scrim[data-scrim="menu"]');
      this.openBtns = $$('[data-menu-open]');
      this.closeBtns = $$('[data-menu-close]');
      if (!this.menu) return;
      this.bind();
    }
    bind() {
      this.openBtns.forEach(b => b.addEventListener('click', () => this.open()));
      this.closeBtns.forEach(b => b.addEventListener('click', () => this.close()));
      if (this.scrim) this.scrim.addEventListener('click', () => this.close());
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.menu.classList.contains('is-open')) this.close();
      });
    }
    open() {
      this.menu.classList.add('is-open');
      if (this.scrim) this.scrim.classList.add('is-active');
      lockScroll();
      this.menu.setAttribute('aria-hidden', 'false');
    }
    close() {
      this.menu.classList.remove('is-open');
      if (this.scrim) this.scrim.classList.remove('is-active');
      unlockScroll();
      this.menu.setAttribute('aria-hidden', 'true');
    }
  }

  /* ============================================================
     Cart drawer — robust, event-delegated, mobile-friendly
     ============================================================ */
  class CartDrawer {
    constructor() {
      this.drawer = $('.cart-drawer');
      this.scrim = $('.scrim[data-scrim="cart"]');
      if (!this.drawer) {
        console.warn('[NOMA] Cart drawer not found in DOM');
        return;
      }
      this.body = this.drawer.querySelector('.cart-drawer__body');
      this.footer = this.drawer.querySelector('.cart-drawer__footer');
      this.countBadges = $$('[data-cart-count]');
      this.isOpen = false;
      this.isLoading = false;
      this.bind();
      // Initial fetch in background — non-blocking
      this.refresh().catch(() => {});
    }
    bind() {
      // Delegated open buttons (works for buttons added dynamically too)
      document.addEventListener('click', (e) => {
        const openBtn = e.target.closest('[data-cart-open]');
        if (openBtn) {
          e.preventDefault();
          this.open();
        }
      });

      // Delegated close — works even for dynamically rendered close buttons
      this.drawer.addEventListener('click', (e) => {
        // Close button
        if (e.target.closest('[data-cart-close]')) {
          e.preventDefault();
          this.close();
          return;
        }
        // Remove item
        const removeBtn = e.target.closest('[data-cart-remove]');
        if (removeBtn) {
          e.preventDefault();
          const item = removeBtn.closest('[data-cart-item]');
          const key = removeBtn.dataset.key || (item && item.dataset.key);
          if (key) this.updateQty(key, 0);
          return;
        }
        // Qty buttons
        const plus = e.target.closest('[data-cart-qty="plus"]');
        const minus = e.target.closest('[data-cart-qty="minus"]');
        if (plus || minus) {
          e.preventDefault();
          const item = e.target.closest('[data-cart-item]');
          if (!item) return;
          const input = item.querySelector('input[data-cart-qty-input]');
          if (!input) return;
          const cur = parseInt(input.value, 10) || 0;
          const next = plus ? cur + 1 : Math.max(0, cur - 1);
          this.updateQty(item.dataset.key, next);
        }
      });

      // Scrim click closes
      if (this.scrim) this.scrim.addEventListener('click', () => this.close());

      // Escape key
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen) this.close();
      });

      // Touch swipe right to close (mobile UX)
      let touchStartX = 0;
      let touchEndX = 0;
      this.drawer.addEventListener('touchstart', (e) => {
        touchStartX = e.changedTouches[0].screenX;
      }, { passive: true });
      this.drawer.addEventListener('touchend', (e) => {
        touchEndX = e.changedTouches[0].screenX;
        if (touchEndX - touchStartX > 80) this.close();
      }, { passive: true });


      // Quick-add depuis les cartes produit (collection)
      document.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-quick-add]');
        if (!btn) return;
        if (btn.closest('.noma-cart-crosssell')) return;
        e.preventDefault();
        const variantId = parseInt(btn.dataset.quickAdd);
        if (!variantId) return;
        btn.textContent = '...';
        btn.disabled = true;
        this.addItem({ id: variantId, quantity: 1 })
          .then(() => {
            btn.textContent = '✓ Ajouté';
            setTimeout(() => { btn.textContent = 'Ajouter au panier'; btn.disabled = false; }, 1800);
          })
          .catch(() => { btn.textContent = 'Ajouter au panier'; btn.disabled = false; });
      });
    }
    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.drawer.classList.add('is-open');
      if (this.scrim) this.scrim.classList.add('is-active');
      lockScroll();
      this.drawer.setAttribute('aria-hidden', 'false');
      // Refresh in background to ensure data is up to date
      this.refresh().catch(() => {});
    }
    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.drawer.classList.remove('is-open');
      if (this.scrim) this.scrim.classList.remove('is-active');
      unlockScroll();
      this.drawer.setAttribute('aria-hidden', 'true');
    }
    async refresh() {
      try {
        const res = await fetch('/cart.js', {
          headers: { 'Accept': 'application/json' },
          credentials: 'same-origin'
        });
        if (!res.ok) throw new Error('Cart fetch failed: ' + res.status);
        const cart = await res.json();
        this.render(cart);
        return cart;
      } catch (e) {
        console.warn('[NOMA] Cart refresh failed:', e);
        return null;
      }
    }
    updateCount(count) {
      this.countBadges.forEach(b => {
        b.textContent = count;
        if (count > 0) { b.hidden = false; b.removeAttribute('hidden'); }
        else { b.hidden = true; }
      });
    }
    render(cart) {
      if (!cart) return;
      this.updateCount(cart.item_count);
      if (!this.body) return;
      if (cart.item_count === 0) {
        this.body.innerHTML = `
          <div class="cart-drawer__empty">
            <div class="cart-drawer__empty-title">${(window.theme && window.theme.strings && window.theme.strings.cartEmpty) || 'Votre panier est vide.'}</div>
            <p class="cart-drawer__empty-text">Découvrez nos pièces, pensées comme la matière à son état naturel.</p>
            <a href="/collections/all" class="btn btn--ghost" data-cart-close>Explorer la collection</a>
          </div>`;
        if (this.footer) this.footer.hidden = true;
      } else {
        this.body.innerHTML = cart.items.map(item => this.itemMarkup(item)).join('');
        if (this.footer) {
          this.footer.hidden = false;
          const total = this.footer.querySelector('[data-cart-total]');
          if (total) total.textContent = formatMoney(cart.total_price);
        }
      }
    }
    itemMarkup(item) {
      // Build image URL safely — Shopify image URLs may or may not have query strings
      let imgHtml = `<div class="cart-item__image"></div>`;
      if (item.featured_image && item.featured_image.url) {
        const baseUrl = item.featured_image.url;
        const sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
        const imgUrl = baseUrl + sep + 'width=160';
        const alt = (item.product_title || item.title || '').replace(/"/g, '&quot;');
        imgHtml = `<img class="cart-item__image" src="${imgUrl}" alt="${alt}" loading="lazy" width="80" height="100">`;
      }

      // Variant title — handle absence of options_with_values gracefully
      let variantTitle = '';
      if (item.options_with_values && item.options_with_values.length > 0) {
        const filtered = item.options_with_values
          .filter(o => o.value && o.value !== 'Default Title')
          .map(o => o.value);
        if (filtered.length > 0) variantTitle = filtered.join(' / ');
      } else if (item.variant_options) {
        const filtered = item.variant_options.filter(v => v && v !== 'Default Title');
        if (filtered.length > 0) variantTitle = filtered.join(' / ');
      }

      const productUrl = item.url || '#';
      const productTitle = (item.product_title || item.title || '').replace(/</g, '&lt;');

      return `
        <div class="cart-item" data-cart-item data-key="${item.key}">
          <a href="${productUrl}">${imgHtml}</a>
          <div class="cart-item__main">
            <a href="${productUrl}" class="cart-item__title">${productTitle}</a>
            ${variantTitle ? `<div class="cart-item__variant">${variantTitle}</div>` : ''}
            <div class="cart-item__price">${formatMoney(item.final_price)}</div>
            <div class="cart-item__qty">
              <button type="button" data-cart-qty="minus" aria-label="Diminuer">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 7h8"/></svg>
              </button>
              <input data-cart-qty-input type="number" value="${item.quantity}" min="0" readonly aria-label="Quantité">
              <button type="button" data-cart-qty="plus" aria-label="Augmenter">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M3 7h8M7 3v8"/></svg>
              </button>
            </div>
          </div>
          <div class="cart-item__side">
            <div class="cart-item__line-price">${formatMoney(item.final_line_price)}</div>
            <button class="cart-item__remove" data-cart-remove data-key="${item.key}" type="button">Retirer</button>
          </div>
        </div>`;
    }
    async updateQty(key, qty) {
      if (this.isLoading) return;
      this.isLoading = true;
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ id: key, quantity: qty })
        });
        if (!res.ok) throw new Error('Cart update failed: ' + res.status);
        const cart = await res.json();
        this.render(cart);
      } catch (e) {
        console.warn('[NOMA] Cart update failed:', e);
      } finally {
        this.isLoading = false;
      }
    }
    async addItem(payload) {
      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          console.warn('[NOMA] Add to cart failed:', err);
          throw new Error('Add failed');
        }
        await this.refresh();
        this.open();
        return true;
      } catch (e) {
        console.error('[NOMA] Add to cart error:', e);
        return false;
      }
    }
  }

  /* ============================================================
     Hero carousel — swipeable, fullscreen
     ============================================================ */
  class HeroCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('.hero__track');
      this.slides = Array.from(root.querySelectorAll('.hero__slide'));
      this.dots = Array.from(root.querySelectorAll('.hero__dot'));
      this.prev = root.querySelector('[data-hero-prev]');
      this.next = root.querySelector('[data-hero-next]');
      this.index = 0;
      this.autoplay = parseInt(root.dataset.autoplay, 10) || 0;
      this.timer = null;
      this.isAnimating = false;

      if (this.slides.length <= 1) return;

      this.bind();
      this.go(0, false);
      if (this.autoplay > 0) this.startAuto();
    }
    bind() {
      if (this.prev) this.prev.addEventListener('click', () => this.go(this.index - 1));
      if (this.next) this.next.addEventListener('click', () => this.go(this.index + 1));
      this.dots.forEach((d, i) => d.addEventListener('click', () => this.go(i)));

      // touch
      let startX = 0, currentX = 0, isDown = false;
      this.track.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX; isDown = true;
        this.pauseAuto();
      }, { passive: true });
      this.track.addEventListener('touchmove', (e) => {
        if (!isDown) return;
        currentX = e.touches[0].clientX;
      }, { passive: true });
      this.track.addEventListener('touchend', () => {
        if (!isDown) return;
        const delta = currentX - startX;
        if (Math.abs(delta) > 50) {
          delta < 0 ? this.go(this.index + 1) : this.go(this.index - 1);
        }
        isDown = false; startX = 0; currentX = 0;
        if (this.autoplay > 0) this.startAuto();
      });

      // pause on hover
      this.root.addEventListener('mouseenter', () => this.pauseAuto());
      this.root.addEventListener('mouseleave', () => { if (this.autoplay > 0) this.startAuto(); });

      // pause when out of view
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) { if (this.autoplay > 0) this.startAuto(); }
          else this.pauseAuto();
        });
      }, { threshold: 0.4 });
      io.observe(this.root);
    }
    go(i, animate = true) {
      const len = this.slides.length;
      this.index = ((i % len) + len) % len;
      this.track.style.transitionDuration = animate ? '900ms' : '0ms';
      this.track.style.transform = `translateX(-${this.index * 100}%)`;
      this.slides.forEach((s, idx) => s.classList.toggle('is-active', idx === this.index));
      this.dots.forEach((d, idx) => {
        d.classList.toggle('is-active', idx === this.index);
        if (idx === this.index && this.autoplay > 0) {
          d.style.setProperty('--hero-duration', `${this.autoplay}ms`);
        }
      });
    }
    startAuto() {
      this.pauseAuto();
      this.timer = setTimeout(() => this.go(this.index + 1), this.autoplay);
    }
    pauseAuto() {
      if (this.timer) { clearTimeout(this.timer); this.timer = null; }
    }
  }

  /* ============================================================
     Color showcase
     ============================================================ */
  class ColorShowcase {
    constructor(root) {
      this.root = root;
      this.swatches = $$('[data-showcase-swatch]', root);
      this.images = $$('[data-showcase-image]', root);
      this.nameEl = $('[data-showcase-name]', root);
      this.bind();
    }
    bind() {
      this.swatches.forEach(sw => {
        sw.addEventListener('click', () => this.select(sw));
        sw.addEventListener('mouseenter', () => this.select(sw));
      });
    }
    select(sw) {
      const key = sw.dataset.showcaseSwatch;
      const bg = sw.dataset.bg;
      const colorName = sw.dataset.name;
      this.swatches.forEach(s => s.classList.toggle('is-active', s === sw));
      this.images.forEach(img => img.classList.toggle('is-active', img.dataset.showcaseImage === key));
      if (this.nameEl && colorName) this.nameEl.textContent = colorName;
      if (bg) this.root.style.backgroundColor = bg;
    }
  }

  /* ============================================================
     Newsletter form (uses Shopify customer endpoint)
     ============================================================ */
  const initNewsletterForms = () => {
    $$('form.newsletter__form').forEach(form => {
      form.addEventListener('submit', () => {
        // Shopify handles via /contact#newsletter natively
        const successEl = form.querySelector('[data-newsletter-success]');
        if (successEl) {
          setTimeout(() => { successEl.hidden = false; }, 50);
        }
      });
    });
  };

  /* ============================================================
     Init
     ============================================================ */
  const init = () => {
    initHeader();
    initReveal();
    new MobileMenu();
    window.cartDrawer = new CartDrawer();
    $$('[data-hero]').forEach(h => new HeroCarousel(h));
    $$('[data-color-showcase]').forEach(s => new ColorShowcase(s));
    initNewsletterForms();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
